/**
 * FirmaSearchSelect.jsx — Firma arama/seçme kutusu
 *
 * Stand popup'larında firmayı ELLE ID YAZARAK atama alışkanlığının yerini alan
 * bileşen. Kutu hem ada hem KISA ADA hem de ID'ye göre arıyor (GET /firma/cards
 * üçünde birden LIKE %term% yapıyor), yani "49858" yazmak eskisi gibi çalışmaya
 * devam ediyor — sadece artık seçmeden önce firmanın adını görüyorsunuz.
 *
 * Servis: services/firmaAramaService.searchFirmalar (bkz.
 * docs/guides/FE-Firma-create-match-search-guide.md bölüm 3)
 *
 * ⚠️ En az 2 karakter + 350 ms debounce ile arıyor. Sebep: bu ailedeki diğer arama
 * ucu (POST /searchFirmaStand) sayfasız ve boş metinde ~29.000 satır dönüyor;
 * /cards sayfalı olduğu için güvenli olsa da her tuş vuruşunda istek atmanın
 * anlamı yok.
 *
 * Props:
 *   value        {firmaId, firmaName, firmaShortname} | null — seçili firma
 *   onChange     (firma|null) => void
 *   onCreateNew  (aranan: string) => void | null — verilirse sonuç listesinin altında
 *                "Yeni Firma Oluştur" satırı çıkar (fuara ilk kez katılan firma). Kutuya yazılmış
 *                (kırpılmış) arama metni argüman olarak gider, yeni firma formunun adı dolsun (#281).
 *   forceLive    canlı backend'e git (mock modu atla)
 *   firmaTypeId  verilirse GET /firma/cards/by-role (backend #22) kullanılır — bu rolü
 *                daha önce taşımış firmalar sonuçların başına gelir. Verilmezse normal
 *                /firma/cards araması (rol-bağımsız).
 *   label/hint/placeholder/disabled/invalid/bsSize
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormGroup, Label, Input, InputGroup, InputGroupText, Spinner, Badge, Button } from "reactstrap";
import { Search, X, Plus, Building2, AlertTriangle } from "lucide-react";
import { searchFirmalar, searchFirmalarByRole } from "../../services/firmaAramaService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 350;
const RESULT_SIZE = 8;

export default function FirmaSearchSelect({
  value = null,
  onChange,
  onCreateNew = null,
  forceLive = false,
  firmaTypeId = null,
  label = null,
  hint = null,
  placeholder = i18n.t("components:firmaArama.firmaAdiKisaAdiVeya"),
  disabled = false,
  invalid = false,
  bsSize = "sm",
}) {
  const { t } = useTranslation("components");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Dışarı tıklayınca sonuç listesi kapansın — modal içinde açık kalan bir liste
  // altındaki alanları örtüyor.
  useEffect(() => {
    function onDocMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  // KURAL: fetch mantığı Effect İÇİNDE tanımlanır ve setState yalnızca async/timer
  // geri çağrılarında çağrılır — react-hooks/set-state-in-effect ihlalini önler
  // (bkz. StandListPage.jsx'teki aynı not).
  useEffect(() => {
    const term = query.trim();
    // Kısa terimde istek atılmıyor VE state temizlenmiyor: gösterim zaten türetiliyor
    // (aşağıdaki `visibleResults`), böylece efekt gövdesinde senkron setState olmuyor.
    if (term.length < MIN_QUERY_LENGTH) return undefined;

    let ignore = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = firmaTypeId != null
          ? await searchFirmalarByRole({ text: term, firmaTypeId, page: 0, size: RESULT_SIZE }, { forceLive })
          : await searchFirmalar({ text: term, page: 0, size: RESULT_SIZE }, { forceLive });
        if (ignore) return;
        setResults(res.content || []);
        setTotal(res.totalElements ?? 0);
        setError(null);
      } catch (e) {
        if (ignore) return;
        setResults([]);
        setTotal(0);
        setError(e?.message || i18n.t("components:firmaArama.firmaAramasiBasarisiz"));
      } finally {
        if (!ignore) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => { ignore = true; clearTimeout(timer); };
  }, [query, forceLive, firmaTypeId]);

  const pick = useCallback((firma) => {
    onChange?.(firma);
    setQuery("");
    setResults([]);
    setOpen(false);
  }, [onChange]);

  const clear = useCallback(() => {
    onChange?.(null);
    setQuery("");
    setResults([]);
    setOpen(false);
  }, [onChange]);

  const term = query.trim();
  const showDropdown = open && (term.length >= MIN_QUERY_LENGTH || !!onCreateNew);
  // Kısa terimde eski sonuçlar TÜRETİLEREK gizleniyor (state sıfırlanmıyor) —
  // yukarıdaki efektin senkron setState'siz kalmasının bedeli bu tek satır.
  const visibleResults = term.length >= MIN_QUERY_LENGTH ? results : [];

  return (
    <FormGroup className="mb-0" tag="div">
      <div ref={wrapRef}>
      {label && <Label size="sm">{label}</Label>}

      {value ? (
        // ── Seçili durum ────────────────────────────────────────────────────────
        <div
          className={`d-flex align-items-center gap-2 border rounded px-2 py-1 ${invalid ? "border-danger" : ""}`}
          style={{ minHeight: 31, backgroundColor: "var(--vz-light)" }}
        >
          <Building2 size={14} className="text-muted flex-shrink-0" />
          <div className="flex-grow-1 text-truncate lh-sm">
            <span className="fw-semibold small">{value.firmaName || t("firmaArama.firma", { firmaId: value.firmaId })}</span>
            {value.firmaShortname && (
              <span className="text-muted small ms-1">({value.firmaShortname})</span>
            )}
          </div>
          {value.isForbidden && (
            <Badge color="danger" pill title={t("firmaArama.buFirmaYasakliOlarakIsaretli")}>
              <AlertTriangle size={10} className="me-1" />{t("firmaArama.yasakli")}
            </Badge>
          )}
          <Badge color="secondary" pill className="flex-shrink-0">#{value.firmaId}</Badge>
          {!disabled && (
            <Button close aria-label={t("firmaArama.secimiKaldir")} onClick={clear} className="flex-shrink-0" />
          )}
        </div>
      ) : (
        // ── Arama durumu ────────────────────────────────────────────────────────
        <div className="position-relative">
          <InputGroup size={bsSize}>
            <InputGroupText><Search size={13} /></InputGroupText>
            <Input
              bsSize={bsSize}
              value={query}
              placeholder={placeholder}
              disabled={disabled}
              invalid={invalid}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
            />
            {loading && <InputGroupText><Spinner size="sm" /></InputGroupText>}
            {query !== "" && !loading && (
              <InputGroupText
                role="button"
                onClick={() => { setQuery(""); setResults([]); }}
                title={t("firmaArama.aramayiTemizle")}
              >
                <X size={13} />
              </InputGroupText>
            )}
          </InputGroup>

          {showDropdown && (
            <div
              className="position-absolute w-100 border rounded shadow-sm mt-1"
              style={{ zIndex: 1060, maxHeight: 260, overflowY: "auto", backgroundColor: "var(--vz-card-bg, #fff)" }}
            >
              {error && (
                <div className="px-2 py-2 small text-danger">
                  <AlertTriangle size={12} className="me-1" />{error}
                </div>
              )}

              {!error && term.length >= MIN_QUERY_LENGTH && !loading && visibleResults.length === 0 && (
                <div className="px-2 py-2 small text-muted">
                  {t("firmaArama.ileEslesenFirmaYok", { term })}
                </div>
              )}

              {term.length < MIN_QUERY_LENGTH && !error && (
                <div className="px-2 py-2 small text-muted">
                  {t("firmaArama.aramakIcinEnAzKarakter", { MIN_QUERY_LENGTH })}
                </div>
              )}

              {visibleResults.map((f) => (
                <button
                  key={f.firmaId}
                  type="button"
                  className="btn btn-link text-decoration-none text-start w-100 px-2 py-1 border-0 rounded-0"
                  onClick={() => pick(f)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div className="flex-grow-1 text-truncate lh-sm">
                      <span className="small fw-semibold text-body">{f.firmaName}</span>
                      {f.firmaShortname && <span className="text-muted small ms-1">({f.firmaShortname})</span>}
                    </div>
                    {f.isForbidden && <Badge color="danger" pill>{t("firmaArama.yasakli")}</Badge>}
                    <Badge color="light" className="text-muted flex-shrink-0">#{f.firmaId}</Badge>
                  </div>
                </button>
              ))}

              {/* Sayfa 0'ın dışında kalan sonuçlar sessizce düşmesin — sayfalama
                  yerine "daralt" diyoruz, seçim kutusu bir liste ekranı değil. */}
              {term.length >= MIN_QUERY_LENGTH && total > visibleResults.length && (
                <div className="px-2 py-1 small text-muted border-top">
                  {t("firmaArama.sonucunIlkTanesiAramayiDaraltin", { total, length: visibleResults.length })}
                </div>
              )}

              {onCreateNew && (
                <button
                  type="button"
                  className="btn btn-link text-decoration-none text-start w-100 px-2 py-1 border-0 border-top rounded-0"
                  onClick={() => { setOpen(false); onCreateNew(term); }}
                >
                  <span className="small fw-semibold text-success">
                    <Plus size={13} className="me-1" />{t("firmaArama.yeniFirmaOlustur")}
                  </span>
                  <span className="text-muted small ms-1">{t("firmaArama.fuaraIlkKezKatiliyorsa")}</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {hint && <div className="text-muted small mt-1">{hint}</div>}
      </div>
    </FormGroup>
  );
}
